import React, { useEffect, useState } from 'react';
import { Ingredient } from '../types';
import DeleteIngredient from './DeleteIngredient/DeleteIngredient';
import { toast } from 'react-toastify';
import { updateIngredient, DeleteIngredientsSelected } from '../../../../../crud/menu_api/ingredients';
import { HiOutlineTrash, HiOutlinePencilSquare, HiCheck, HiBuildingStorefront } from 'react-icons/hi2';
import { FaCogs, FaLayerGroup, FaSearch } from 'react-icons/fa';
import CreateIngredient from '../components/CreateIngredient/CreateIngredient';
import UpdateIngredient from '../components/UpdateIngredient/UpdateIngredient';
import FilterBar from './Table/components/FilterBar/FilterBar';

interface TableProps {
  ingredients: any[];
  setIngredients: (data: any[]) => void;
  originalIngredients: Ingredient[];
  setOriginalIngredients: (data: Ingredient[]) => void;
  showMobileFilters?: boolean;
  onCloseMobileFilters?: () => void;
}

const Table: React.FC<TableProps> = ({
  ingredients,
  setIngredients,
  originalIngredients,
  setOriginalIngredients,
  showMobileFilters,
  onCloseMobileFilters,
}) => {
  const [localIngredients, setLocalIngredients] = useState<Ingredient[]>(ingredients);
  const [filteredIngredients, setFilteredIngredients] = useState<Ingredient[]>(ingredients);
  const [editNameId, setEditNameId] = useState<number | null>(null);
  const [editedName, setEditedName] = useState<string>("");

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [showOnlyInactive, setShowOnlyInactive] = useState(false);
  const [showOnlyWithStock, setShowOnlyWithStock] = useState(false);
  const [showOnlyNeedProduction, setShowOnlyNeedProduction] = useState(false);
  const [showOnlyNoNeedProduction, setShowOnlyNoNeedProduction] = useState(false);
  const [showOnlyComposite, setShowOnlyComposite] = useState(false);
  const [showOnlyNonComposite, setShowOnlyNonComposite] = useState(false);

  const [showSelection, setShowSelection] = useState(false);
  const [selectedIngredients, setSelectedIngredients] = useState<Ingredient[]>([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentIngredientId, setCurrentIngredientId] = useState<number | null>(null);

  const [isMobile, setIsMobile] = useState(false);


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

useEffect(() => {
  setLocalIngredients([...ingredients]);
  setFilteredIngredients([...ingredients]);
}, [ingredients]);

// Apply filtering logic
useEffect(() => {
  let filtered = [...localIngredients];

  // Text search filter
  if (searchTerm.trim()) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(ing =>
      ing.ingredient_name?.toLowerCase().includes(term)
    );
  }

  // Filter by active state
  if (showOnlyActive) {
    filtered = filtered.filter(ing => ing.state === true);
  }

  // Filter by inactive state
  if (showOnlyInactive) {
    filtered = filtered.filter(ing => ing.state === false);
  }

  // Filter by stock control
  if (showOnlyWithStock) {
    filtered = filtered.filter(ing => ing.stock_control === true);
  }

  // Filter by need production
  if (showOnlyNeedProduction) {
    filtered = filtered.filter(ing => ing.need_production === true);
  }

  // Filter by no need production
  if (showOnlyNoNeedProduction) {
    filtered = filtered.filter(ing => ing.need_production === false);
  }

  // Filter by composite (has ingredients)
  if (showOnlyComposite) {
    filtered = filtered.filter(ing => ing.has_ingredients === true);
  }

  // Filter by non-composite (doesn't have ingredients)
  if (showOnlyNonComposite) {
    filtered = filtered.filter(ing => ing.has_ingredients === false);
  }

  setFilteredIngredients(filtered);
}, [localIngredients, searchTerm, showOnlyActive, showOnlyInactive, showOnlyWithStock, showOnlyNeedProduction, showOnlyNoNeedProduction, showOnlyComposite, showOnlyNonComposite]);

  const handleSelectIngredient = (selectedIngredient: Ingredient) => {
    setSelectedIngredients(prev =>
      prev.some(i => i.ingredient_id === selectedIngredient.ingredient_id)
        ? prev.filter(i => i.ingredient_id !== selectedIngredient.ingredient_id)
        : [...prev, selectedIngredient]
    );
  };

  const deleteItem = (id: number) => {
    setCurrentIngredientId(id);
    setShowDeleteModal(true);
  };

  const handleNameEdit = (ingredient: Ingredient) => {
    setEditNameId(ingredient.ingredient_id);
    setEditedName(ingredient.ingredient_name);
  };

  const saveName = (id: number) => {
    const toUpdate = originalIngredients.find(ing => ing.ingredient_id === id);
    if (!toUpdate) {
      toast.error('Ingrediente no encontrado en la lista original.');
      setEditNameId(null);
      return;
    }
    const updatedOriginal = originalIngredients.filter(ing => ing.ingredient_id !== id);
    updateIngredient(id, { ingredient_name: editedName })
      .then(response => {
        const updatedIngredient: Ingredient = (response as any).data;
        const newList = [updatedIngredient, ...updatedOriginal];
        setLocalIngredients(newList);
        setIngredients(newList);
        setOriginalIngredients(newList);
        toast.success('Nombre del ingrediente actualizado correctamente');
      })
      .catch(() => toast.error('Error al actualizar el nombre del ingrediente'))
      .finally(() => setEditNameId(null));
  };

  const toggleItemState = (id: number) => {
    const updatedOriginal = originalIngredients.filter(ing => ing.ingredient_id !== id);
    updateIngredient(id, { state: !localIngredients.find(i => i.ingredient_id === id)?.state })
      .then(response => {
        const updatedIngredient: Ingredient = (response as any).data;
        const newList = [updatedIngredient, ...updatedOriginal];
        setLocalIngredients(newList);
        setIngredients(newList);
        setOriginalIngredients(newList);
        toast.success('Estado del ingrediente actualizado correctamente');
      })
      .catch(() => toast.error('Error al actualizar el estado del ingrediente'));
  };

  const openUpdateIngredientModal = (id: number) => {
    setCurrentIngredientId(id);
    setShowUpdateModal(true);
  };

  const handleDeleteSelected = async () => {
    const ingredientIds = selectedIngredients.map(i => i.ingredient_id.toString());
    try {
      await DeleteIngredientsSelected(ingredientIds);
      const originalIds = selectedIngredients.map(i => i.ingredient_id);
      const updatedIngredients = ingredients.filter(i => !originalIds.includes(i.ingredient_id));
      setIngredients(updatedIngredients);
      setOriginalIngredients(updatedIngredients);
      setSelectedIngredients([]);
      toast.success("Ingredientes seleccionados eliminados correctamente");
    } catch {
      toast.error("Error al eliminar ingredientes seleccionados");
    }
  };

  const renderTable = () => (
    <div className="mp-table-card">
      <div style={{ overflowY: 'auto', maxHeight: '65vh' }}>
      <table className="mp-table" style={{ width: '100%' }}>
        <thead className="mp-table-head">
          <tr>
            {showSelection && <th style={{ width: 40 }}></th>}
            <th>Nombre</th>
            <th style={{ width: 80, textAlign: 'center' }}>Activo</th>
            <th style={{ width: 130, textAlign: 'center' }}>Stock Control</th>
            {!isMobile && <th style={{ width: 160, textAlign: 'center' }}>Necesita Producción</th>}
            {!isMobile && <th style={{ width: 130, textAlign: 'center' }}>Es Compuesto</th>}
            <th style={{ width: 100, textAlign: 'center' }}>Acciones</th>
          </tr>
        </thead>
        <tbody className="mp-table-body">
          {filteredIngredients.map((ing) => (
            <tr key={ing.ingredient_id}>
              {showSelection && (
                <td style={{ textAlign: 'center' }}>
                  <div className="mp-radio-button">
                    <input
                      id={`checkbox-${ing.ingredient_id}`}
                      type="checkbox"
                      name="isSelected"
                      checked={selectedIngredients.some(i => i.ingredient_id === ing.ingredient_id)}
                      onChange={() => handleSelectIngredient(ing)}
                    />
                    <label htmlFor={`checkbox-${ing.ingredient_id}`}></label>
                  </div>
                </td>
              )}
              <td>
                {editNameId === ing.ingredient_id ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editedName}
                      className="mp-input-table"
                      onChange={e => setEditedName(e.target.value)}
                      autoFocus
                      style={{ flex: 1 }}
                    />
                    <button className="mp-btn-icon" onClick={() => saveName(ing.ingredient_id)} title="Confirmar" style={{ color: '#22C55E' }}>
                      <HiCheck size={16} />
                    </button>
                  </div>
                ) : (
                  <span onClick={() => handleNameEdit(ing)} style={{ cursor: 'pointer', fontWeight: 500, color: 'var(--text-color)' }}>
                    {ing.ingredient_name}
                  </span>
                )}
              </td>
              <td style={{ textAlign: 'center' }}>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={ing.state}
                    onChange={() => toggleItemState(ing.ingredient_id)}
                  />
                  <span className="slider round"></span>
                </label>
              </td>
              <td style={{ textAlign: 'center' }}>
                <span className={`mp-badge ${ing.stock_control ? 'mp-badge-success' : 'mp-badge-neutral'}`}>
                  <HiBuildingStorefront />
                  {!isMobile && (ing.stock_control ? 'Activado' : 'Desactivado')}
                </span>
              </td>
              {!isMobile && (
                <td style={{ textAlign: 'center' }}>
                  <span className={`mp-badge ${ing.need_production ? 'mp-badge-success' : 'mp-badge-neutral'}`}>
                    <FaCogs style={{ fontSize: '0.625rem' }} />
                    {ing.need_production ? 'Sí' : 'No'}
                  </span>
                </td>
              )}
              {!isMobile && (
                <td style={{ textAlign: 'center' }}>
                  <span className={`mp-badge ${ing.has_ingredients ? 'mp-badge-amber' : 'mp-badge-neutral'}`}>
                    <FaLayerGroup style={{ fontSize: '0.625rem' }} />
                    {ing.has_ingredients ? 'Compuesto' : 'Simple'}
                  </span>
                </td>
              )}
              <td style={{ textAlign: 'center' }}>
                <div className="flex items-center justify-center gap-1.5">
                  <button className="mp-btn-icon" onClick={() => openUpdateIngredientModal(ing.ingredient_id)} title="Editar">
                    <HiOutlinePencilSquare size={16} />
                  </button>
                  <button className="mp-btn-icon mp-btn-icon-danger" onClick={() => deleteItem(ing.ingredient_id)} title="Eliminar">
                    <HiOutlineTrash size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );

  return (
    <>
      <FilterBar
        showOnlyActive={showOnlyActive}
        onActiveFilterChange={setShowOnlyActive}
        showOnlyInactive={showOnlyInactive}
        onInactiveFilterChange={setShowOnlyInactive}
        showOnlyWithStock={showOnlyWithStock}
        onStockFilterChange={setShowOnlyWithStock}
        showOnlyNeedProduction={showOnlyNeedProduction}
        onNeedProductionFilterChange={setShowOnlyNeedProduction}
        showOnlyNoNeedProduction={showOnlyNoNeedProduction}
        onNoNeedProductionFilterChange={setShowOnlyNoNeedProduction}
        showOnlyComposite={showOnlyComposite}
        onCompositeFilterChange={setShowOnlyComposite}
        showOnlyNonComposite={showOnlyNonComposite}
        onNonCompositeFilterChange={setShowOnlyNonComposite}
        showSelection={showSelection}
        selectedIngredientsCount={selectedIngredients.length}
        onCreateIngredient={() => setShowCreateModal(true)}
        onToggleSelection={() => {
          if (showSelection) {
            setSelectedIngredients([]);
          }
          setShowSelection(prev => !prev);
        }}
        onDeleteSelection={showSelection && selectedIngredients.length > 0 ? handleDeleteSelected : undefined}
        searchBar={
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xs opacity-40" style={{ color: 'var(--text-color)' }} />
            <input
              type="text"
              placeholder="Buscar ingredientes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mp-form-input"
              style={{ paddingLeft: '2rem' }}
            />
          </div>
        }
        showMobileFilters={showMobileFilters}
        onCloseMobileFilters={onCloseMobileFilters}
      />
      
      {renderTable()}

      <CreateIngredient
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        ingredients={localIngredients}
        setIngredients={setLocalIngredients}
        originalIngredients={originalIngredients}
        setIngredient={setIngredients}
        setOriginalIngredients={setOriginalIngredients}
      />
      <DeleteIngredient
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        ingredientId={currentIngredientId}
        ingredients={localIngredients}
        setIngredients={setLocalIngredients}
        originalIngredients={originalIngredients}
        setIngredient={setIngredients}
        setOriginalIngredients={setOriginalIngredients}
      />
      <UpdateIngredient
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        currentIngredientId={currentIngredientId}
        ingredients={localIngredients}
        setIngredients={setLocalIngredients}
        originalIngredients={originalIngredients}
        setIngredient={setIngredients}
        setOriginalIngredients={setOriginalIngredients}
      />
    </>
  );
};

export default Table;
